import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Card, CardTitle, CardValue } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { MoneyInput } from '@/components/money/MoneyInput';
import { PaymentForm } from '@/components/debts/PaymentForm';
import { ProjectionSummary } from '@/components/debts/ProjectionSummary';
import { formatCOP, formatDate, formatRate } from '@/lib/format';
import { CHARGE_KIND_LABELS, DEBT_STATUS_LABELS, DEBT_TYPE_LABELS } from '@shared/labels';
import { useDebt, useDebtCharges, useDebtPayments } from '@/hooks/useDebts';
import { useDebtSchedule } from '@/hooks/useProjection';

export function DebtDetailPage() {
  const { id } = useParams<{ id: string }>();
  const debtId = id ? Number(id) : undefined;
  const { data: debt, isLoading } = useDebt(debtId);
  const { data: payments } = useDebtPayments(debtId);
  const { data: charges } = useDebtCharges(debtId);

  const [simulatedPayment, setSimulatedPayment] = useState(0);
  const { data: schedule, isFetching: schedulePending } = useDebtSchedule(
    debtId,
    simulatedPayment > 0 ? simulatedPayment : undefined,
  );

  if (isLoading || !debt) {
    return <p className="text-sm text-muted-foreground">Cargando…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold">{debt.name}</h1>
          <p className="text-sm text-muted-foreground">
            {debt.creditor ? `${debt.creditor} · ` : ''}
            {DEBT_TYPE_LABELS[debt.type]}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge tone={debt.status === 'ACTIVE' ? 'debt' : 'muted'}>{DEBT_STATUS_LABELS[debt.status]}</Badge>
          <Link to={`/deudas/${debt.id}/editar`}>
            <Button variant="outline" size="sm">
              <Pencil size={14} /> Editar
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardTitle>Saldo actual</CardTitle>
          <CardValue className="text-debt">{formatCOP(debt.currentBalance)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Tasa</CardTitle>
          <CardValue>
            {formatRate(debt.interestRate)} {debt.interestRateType}
          </CardValue>
        </Card>
        <Card>
          <CardTitle>Interés del mes</CardTitle>
          <CardValue>{formatCOP(debt.monthlyInterest)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Inicio</CardTitle>
          <CardValue className="text-base">{formatDate(debt.startDate)}</CardValue>
        </Card>
        {debt.creditLimit != null && (
          <Card>
            <CardTitle>Cupo disponible</CardTitle>
            <CardValue>{formatCOP(Math.max(0, debt.creditLimit - debt.currentBalance))}</CardValue>
            <p className="text-xs text-muted-foreground">
              de {formatCOP(debt.creditLimit)} · {Math.round((debt.currentBalance / debt.creditLimit) * 100)}% usado
            </p>
          </Card>
        )}
        {debt.minimumPayment != null && (
          <Card>
            <CardTitle>Pago mínimo</CardTitle>
            <CardValue>{formatCOP(debt.minimumPayment)}</CardValue>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="card">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-medium">Proyección de pago</h2>
              <div className="w-full sm:w-48">
                <MoneyInput value={simulatedPayment} onChange={setSimulatedPayment} placeholder="Abono mensual a simular" />
              </div>
            </div>
            {schedulePending ? (
              <p className="text-sm text-muted-foreground">Calculando…</p>
            ) : schedule ? (
              <ProjectionSummary result={schedule} />
            ) : null}
          </div>

          <div className="card">
            <h2 className="mb-3 text-sm font-medium">Historial de compras y cargos</h2>
            {charges && charges.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {charges.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 border-b border-border pb-2 last:border-0">
                    <div className="min-w-0">
                      <p className="truncate">{c.note || CHARGE_KIND_LABELS[c.kind]}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                        <span>{formatDate(c.chargedAt)}</span>
                        {c.kind === 'PURCHASE' && c.installmentsTotal != null && (
                          <Badge tone="muted">{c.installmentsTotal} cuotas</Badge>
                        )}
                        {c.kind === 'PURCHASE' && !c.generatesInterest && <Badge tone="income">Sin interés</Badge>}
                      </div>
                    </div>
                    <span className="shrink-0 font-medium text-debt">{formatCOP(c.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Aún no hay compras ni cargos registrados.</p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="card">
            <h2 className="mb-3 text-sm font-medium">Registrar abono</h2>
            <PaymentForm debt={debt} />
          </div>

          <div className="card">
            <h2 className="mb-3 text-sm font-medium">Historial de abonos</h2>
            {payments && payments.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {payments.map((p) => (
                  <li key={p.id} className="flex justify-between border-b border-border pb-2 last:border-0">
                    <span className="text-muted-foreground">{formatDate(p.paidAt)}</span>
                    <span className="font-medium">{formatCOP(p.amount)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Aún no hay abonos registrados.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
